import React, { useState } from "react";

import {
  faCircleInfo,
  faMagnifyingGlass,
} from "@fortawesome/pro-regular-svg-icons";
import { createColumnHelper, SortingState } from "@tanstack/react-table";
import { twJoin } from "tailwind-merge";

import { LOCK_NODE_EDITOR_CLASSNAME } from "src/authoringMultiplayerLock/constants";
import { Option, Select } from "src/base-components/Select";
import { SortableColumnHeader } from "src/base-components/SortableColumnHeader";
import { Switch } from "src/base-components/Switch";
import {
  Cell,
  ColumnVisibilityDropdown,
  Header,
  TableComp,
} from "src/base-components/Table";
import { Icon } from "src/design-system/Icon";
import { Modal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { TIER_LEVELS } from "src/genai/ModelSelector/constants";
import { isOlderModel } from "src/genai/ModelSelector/facets";
import { ModelItem } from "src/genai/ModelSelector/ModelItem";
import { ResidencyBadges } from "src/genai/ModelSelector/ModelResidencyBadges";
import {
  ModelOption,
  ProviderFilter,
  RegionFilter,
} from "src/genai/ModelSelector/types";
import { formatCompactTokens } from "src/utils/agents/numbers";
import { dateFromNow } from "src/utils/datetime";

const REGION_OPTIONS: Option[] = [
  { key: "any", value: "All regions" },
  { key: "eu", value: "EU" },
  { key: "us", value: "US" },
];

const PROVIDER_OPTIONS: Option[] = [
  { key: "taktile", value: "Taktile" },
  { key: "openai", value: "OpenAI" },
  { key: "google", value: "Google" },
  { key: "byok", value: "Your Key" },
];

// ── value formatting + filtering (table-only) ──────────────────────────────

// Clamp into [0, TIER_LEVELS]; a true 0 (or a score rounding to 0) shows an
// empty meter rather than being inflated to tier 1.
const toTier = (value: number): number =>
  Math.min(TIER_LEVELS, Math.max(0, Math.round(value)));

/** Cost of a typical node run (~20k input + 5k output tokens), in USD. */
const runCost = (model: ModelOption): number | null => {
  if (model.inputCostPer1MTokens == null || model.outputCostPer1MTokens == null)
    return null;
  return (
    (20 * model.inputCostPer1MTokens + 5 * model.outputCostPer1MTokens) / 1000
  );
};

const formatRunCost = (model: ModelOption): string => {
  const cost = runCost(model);
  if (cost == null) return "—";
  return cost < 0.01 ? `$${cost.toFixed(4)}` : `$${cost.toFixed(3)}`;
};

const costTooltip = (model: ModelOption): string | undefined =>
  model.inputCostPer1MTokens != null && model.outputCostPer1MTokens != null
    ? `$${model.inputCostPer1MTokens} in · $${model.outputCostPer1MTokens} out per 1M tokens. ~${formatRunCost(model)} per run.`
    : undefined;

/** Monotonic key for sorting by release recency (larger = newer). */
const releaseSortKey = (isoDate: string): number => {
  const [year, month] = isoDate.split("-").map(Number);
  return year * 12 + (month ?? 1);
};

const passesRegion = (model: ModelOption, region: RegionFilter): boolean => {
  if (region === "any") return true;
  // BYOK residency follows the customer's config — not ours to assert.
  if (model.byok) return false;
  return model.region === region;
};

/** The provider category a model belongs to, for the provider filter. */
const providerOf = (
  model: ModelOption,
): Exclude<ProviderFilter, "any"> | null => {
  if (model.byok) return "byok";
  // Taktile-hosted (Bedrock) is marked by `hosting`, not the model's maker.
  if (model.hosting === "aws") return "taktile";
  if (model.provider === "openai") return "openai";
  if (model.provider === "google_vertex") return "google";
  return null;
};

const passesProvider = (
  model: ModelOption,
  provider: ProviderFilter,
): boolean => provider === "any" || providerOf(model) === provider;

const matchesSearch = (model: ModelOption, query: string): boolean => {
  const normalized = query.trim().toLowerCase();
  if (!normalized) return true;
  return `${model.displayName} ${model.provider} ${model.deploymentLabel ?? ""}`
    .toLowerCase()
    .includes(normalized);
};

// ── cells ──────────────────────────────────────────────────────────────────

const TIER_STEPS = Array.from({ length: TIER_LEVELS }, (_, i) => i + 1);

type TierVariant = "indigo" | "green";

const TIER_FILL: Record<TierVariant, string> = {
  indigo: "bg-indigo-500",
  green: "bg-green-500",
};

/** A filled-tile meter for a 0–5 score, colored by `variant`. */
const TierMeter: React.FC<{ level: number; variant: TierVariant }> = ({
  level,
  variant,
}) => (
  <span className="inline-flex items-center gap-0.5">
    {TIER_STEPS.map((step) => (
      <span
        key={step}
        className={twJoin(
          "h-2.5 w-1.5 rounded-sm",
          step > level ? "bg-gray-200" : TIER_FILL[variant],
        )}
      />
    ))}
  </span>
);

const NumberValue: React.FC<{ children: React.ReactNode }> = ({ children }) => (
  <span className="font-code-12 text-gray-700">{children}</span>
);

const CostCell: React.FC<{ rate: number | null; tooltip?: string }> = ({
  rate,
  tooltip,
}) => {
  if (rate == null) {
    return <span className="font-code-12 text-gray-400">N/A</span>;
  }
  const value = (
    <span className="font-code-12 text-gray-700">{`$${rate}`}</span>
  );
  return tooltip ? (
    <Tooltip body={tooltip} placement="top" asChild>
      {value}
    </Tooltip>
  ) : (
    value
  );
};

/** Model column: the model row, gated behind a tooltip when disabled. */
const ModelNameCell: React.FC<{
  model: ModelOption;
  isDisabled: boolean;
  disabledReason?: string;
}> = ({ model, isDisabled, disabledReason }) => {
  const item = (
    <ModelItem
      model={model}
      outdated={isOlderModel(model)}
      showResidency={false}
    />
  );

  if (isDisabled && disabledReason) {
    return (
      <Tooltip
        body={disabledReason}
        placement="top"
        title="Model not available"
        asChild
      >
        {item}
      </Tooltip>
    );
  }

  return item;
};

// ── columns ────────────────────────────────────────────────────────────────

const columnHelper = createColumnHelper<ModelOption>();

const buildCompareColumns = () => [
  columnHelper.accessor("displayName", {
    id: "model",
    meta: { displayName: "Model" },
    enableHiding: false,
    minSize: 280,
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="compare-sort-model"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Model
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <ModelNameCell
          disabledReason={info.row.original.disabledReason}
          isDisabled={Boolean(info.row.original.disabled)}
          model={info.row.original}
        />
      </Cell>
    ),
  }),
  columnHelper.accessor(
    (model) => `${model.region ?? "zz"}-${model.hosting ?? "zz"}`,
    {
      id: "residency",
      meta: { displayName: "Residency" },
      minSize: 150,
      header: (info) => (
        <Header info={info}>
          <SortableColumnHeader
            dataLoc="compare-sort-residency"
            onClick={info.column.getToggleSortingHandler()}
            sortDirection={info.column.getIsSorted()}
          >
            Residency
          </SortableColumnHeader>
        </Header>
      ),
      cell: (info) => (
        <Cell info={info}>
          <span className="flex min-w-0 items-center gap-1.5 overflow-hidden">
            <ResidencyBadges model={info.row.original} />
          </span>
        </Cell>
      ),
    },
  ),
  columnHelper.accessor((model) => toTier(model.intelligence), {
    id: "intelligence",
    meta: { displayName: "Intelligence" },
    sortDescFirst: true,
    header: (info) => (
      <Header info={info}>
        <Tooltip
          body="Relative reasoning and task performance across these models, based on public benchmarks. More bars = stronger on difficult tasks."
          placement="top"
          asChild
        >
          <SortableColumnHeader
            dataLoc="compare-sort-intelligence"
            onClick={info.column.getToggleSortingHandler()}
            sortDirection={info.column.getIsSorted()}
          >
            Intelligence
          </SortableColumnHeader>
        </Tooltip>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <TierMeter level={info.getValue()} variant="indigo" />
      </Cell>
    ),
  }),
  columnHelper.accessor((model) => toTier(model.speed), {
    id: "speed",
    meta: { displayName: "Speed" },
    sortDescFirst: true,
    header: (info) => (
      <Header info={info}>
        <Tooltip
          body="Relative responsiveness across these models, combining time-to-first-token and tokens per second. More bars = faster."
          placement="top"
          asChild
        >
          <SortableColumnHeader
            dataLoc="compare-sort-speed"
            onClick={info.column.getToggleSortingHandler()}
            sortDirection={info.column.getIsSorted()}
          >
            Speed
          </SortableColumnHeader>
        </Tooltip>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <TierMeter level={info.getValue()} variant="green" />
      </Cell>
    ),
  }),
  columnHelper.accessor((model) => model.outputCostPer1MTokens, {
    id: "outputCost",
    meta: { displayName: "Output cost" },
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="compare-sort-outputCost"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Output $/1M
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <CostCell
          rate={info.getValue()}
          tooltip={costTooltip(info.row.original)}
        />
      </Cell>
    ),
  }),
  columnHelper.accessor((model) => model.inputCostPer1MTokens, {
    id: "inputCost",
    meta: { displayName: "Input cost" },
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="compare-sort-inputCost"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Input $/1M
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <CostCell
          rate={info.getValue()}
          tooltip={costTooltip(info.row.original)}
        />
      </Cell>
    ),
  }),
  columnHelper.accessor((model) => model.contextWindowTokens, {
    id: "context",
    meta: { displayName: "Context window" },
    sortDescFirst: true,
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="compare-sort-context"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Context
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <NumberValue>{formatCompactTokens(info.getValue())}</NumberValue>
      </Cell>
    ),
  }),
  columnHelper.accessor((model) => model.tokensPerMinuteQuota ?? -1, {
    id: "quota",
    meta: { displayName: "Quota (TPM)" },
    sortDescFirst: true,
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="compare-sort-quota"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Quota
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => {
      const quota = info.row.original.tokensPerMinuteQuota;
      return (
        <Cell info={info}>
          <NumberValue>
            {quota != null ? `${formatCompactTokens(quota)} TPM` : "—"}
          </NumberValue>
        </Cell>
      );
    },
  }),
  columnHelper.accessor((model) => releaseSortKey(model.releaseDate), {
    id: "released",
    meta: { displayName: "Released" },
    sortDescFirst: true,
    header: (info) => (
      <Header info={info}>
        <SortableColumnHeader
          dataLoc="compare-sort-released"
          onClick={info.column.getToggleSortingHandler()}
          sortDirection={info.column.getIsSorted()}
        >
          Released
        </SortableColumnHeader>
      </Header>
    ),
    cell: (info) => (
      <Cell info={info}>
        <span className="font-inter-normal-12px text-gray-600">
          {dateFromNow(info.row.original.releaseDate)}
        </span>
      </Cell>
    ),
  }),
  columnHelper.display({
    id: "view-options",
    meta: { hideSkeleton: true },
    minSize: 48,
    maxSize: 48,
    header: (info) => (
      <div className="flex justify-end pr-1.5">
        <ColumnVisibilityDropdown
          dataLoc="compare-models"
          min={2}
          table={info.table}
        />
      </div>
    ),
    cell: () => null,
  }),
];

type CompareModelsModalProps = {
  isOpen: boolean;
  onClose: () => void;
  afterLeave?: () => void;
  /** Models with their rich comparison metadata. */
  models: ModelOption[];
  selectedModelId?: string;
  /** Called with the chosen model id; the modal closes on selection. */
  onSelect: (modelId: string) => void;
  /** Read-only view: comparison is browsable but no model can be selected. */
  isReadOnly?: boolean;
  defaultRegion?: RegionFilter;
  defaultProvider?: ProviderFilter;
  dataLoc?: string;
};

/**
 * The "Compare models" modal: a sortable data table with column visibility and
 * residency filters. Selecting a row chooses that model and closes the modal.
 */
export const CompareModelsModal: React.FC<CompareModelsModalProps> = ({
  isOpen,
  onClose,
  afterLeave,
  models,
  selectedModelId,
  onSelect,
  isReadOnly = false,
  defaultRegion = "any",
  defaultProvider = "any",
  dataLoc = "compare-models",
}) => {
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState<RegionFilter>(defaultRegion);
  const [provider, setProvider] = useState<ProviderFilter>(defaultProvider);
  const [showOutdated, setShowOutdated] = useState(false);
  const [sorting, setSorting] = useState<SortingState>([
    { id: "intelligence", desc: true },
  ]);

  const isModelDisabled = (model: ModelOption) =>
    isReadOnly || Boolean(model.disabled);

  // Only offer provider options that exist in the catalog (e.g. "Your Key"
  // shows only when there's a custom key).
  const presentProviders = new Set<string>(
    models
      .map(providerOf)
      .filter(
        (category): category is Exclude<ProviderFilter, "any"> =>
          category !== null,
      ),
  );
  const providerOptions: Option[] = [
    { key: "any", value: "All providers" },
    ...PROVIDER_OPTIONS.filter((option) => presentProviders.has(option.key)),
  ];

  const filtered = models.filter(
    (model) =>
      passesRegion(model, region) &&
      passesProvider(model, provider) &&
      matchesSearch(model, search),
  );

  // The current selection stays in the table whatever its lifecycle, so the row
  // the user is comparing against never disappears behind the toggle.
  const isTucked = (model: ModelOption) =>
    isOlderModel(model) && model.id !== selectedModelId;

  const tuckedCount = filtered.filter(isTucked).length;
  const rows = showOutdated ? filtered : filtered.filter((m) => !isTucked(m));

  const columns = buildCompareColumns();

  const handleRowClick = (model: ModelOption) => {
    if (isModelDisabled(model)) return;
    onSelect(model.id);
    onClose();
  };

  return (
    <Modal afterLeave={afterLeave} open={isOpen} size="lg" onClose={onClose}>
      <Modal.Header description="Compare capabilities, cost, and residency across the available models.">
        Compare models
      </Modal.Header>
      <Modal.Content noScroll>
        {/*
          Mark the modal content as part of the node editor's lockable-focus
          region. The modal renders in a portal (outside the editor), so without
          this, picking a BYOK model here would drop lockable focus, flip the
          editor reactive, and reset `model` from saved node data — dropping the
          just-picked credential_id. Mirrors the dropdown's useLockNodeEditorClassName.
          Inert outside the node editor (e.g. the agents version page has no lock
          tracker).
        */}
        <div
          className={twJoin(
            "flex flex-col gap-3 py-4",
            LOCK_NODE_EDITOR_CLASSNAME,
          )}
        >
          <div
            className="flex h-12 items-center border-gray-200 border-b"
            data-loc={`${dataLoc}-toolbar`}
          >
            <div className="flex h-full items-center gap-2 pl-3.5">
              <Icon icon={faMagnifyingGlass} size="xs" />
              <input
                className="h-full w-56 bg-transparent font-inter-normal-12px text-gray-800 placeholder:text-gray-500 focus:outline-none"
                data-loc={`${dataLoc}-search`}
                placeholder="Search models"
                value={search}
                onChange={(event) => setSearch(event.target.value)}
              />
            </div>
            <div className="ml-auto flex items-center gap-3 pr-3.5">
              <div className="w-40">
                <Select
                  dataLoc={`${dataLoc}-region`}
                  options={REGION_OPTIONS}
                  value={region}
                  useLockNodeEditorClassName
                  onChange={(value) => setRegion(value as RegionFilter)}
                />
              </div>
              <div className="w-44">
                <Select
                  dataLoc={`${dataLoc}-provider`}
                  options={providerOptions}
                  value={provider}
                  useLockNodeEditorClassName
                  onChange={(value) => setProvider(value as ProviderFilter)}
                />
              </div>
            </div>
          </div>
          <TableComp
            columns={columns}
            columnVisibility={{
              inputCost: false,
              quota: false,
              released: false,
            }}
            data={rows}
            dataLoc={`${dataLoc}-table`}
            disableVirtualization
            emptyState={
              <div className="py-10 text-center font-inter-normal-13px text-gray-500">
                {tuckedCount > 0
                  ? "No current models match your filters."
                  : "No models match your filters."}
              </div>
            }
            // Scroll happens on TableComp's own container
            frameClassName="max-h-128"
            getRowId={(model) => model.id}
            sorting={sorting}
            onSortingChange={setSorting}
            rowPropsGetter={(row) => ({
              classNameOverrides: twJoin(
                "transition-colors",
                isModelDisabled(row.original)
                  ? "cursor-default opacity-60"
                  : "cursor-pointer hover:bg-gray-50",
                row.original.id === selectedModelId && "bg-indigo-50",
              ),
              onClick: () => handleRowClick(row.original),
            })}
            uiStoreKey="model-compare-columns"
          />
        </div>
      </Modal.Content>
      {/*
        The toggle is a footer control because `Modal.Content` is `noScroll`
        (overflow-hidden) and the table already grows to the panel's max height:
        anything laid out after the table inside the content is clipped away,
        with no scrollbar to reach it.

        The footer sits outside the content's lockable-focus region, so the row
        carries the marker class itself. Focusing the switch would otherwise
        count as focus leaving the region and drop the node editor's lock, with
        the same consequences the content comment above describes.
      */}
      <Modal.Footer primaryButton={false} secondaryButton="Close">
        {tuckedCount > 0 && (
          <div
            className={twJoin(
              "flex items-center gap-2",
              LOCK_NODE_EDITOR_CLASSNAME,
            )}
            data-loc={`${dataLoc}-outdated-row`}
          >
            <Switch
              dataLoc={`${dataLoc}-outdated-toggle`}
              enabled={showOutdated}
              onChange={setShowOutdated}
            />
            <span className="font-inter-normal-12px text-gray-600">
              {`Show outdated models (${tuckedCount})`}
            </span>
            <Tooltip
              body="Superseded or deprecated models. They still run, but a newer model in the same line is usually more accurate and often cheaper."
              placement="top"
              asChild
            >
              <span className="flex items-center">
                <Icon icon={faCircleInfo} size="xs" />
              </span>
            </Tooltip>
          </div>
        )}
      </Modal.Footer>
    </Modal>
  );
};
